/**
 * The chips under a dictation prompt's instructions: each adds or removes its
 * sentence (`prompt-chips.ts`), and is on while that sentence is in the text.
 * Translate opens a menu of languages instead, and names the one it writes.
 */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icons,
  ToggleChip,
} from "@/components/ui";
import {
  chipsIn,
  PROMPT_CHIPS,
  toggleChip,
  translateLanguageIn,
  TRANSLATE_LANGUAGES,
} from "@/desktop/dictation-anywhere/prompt-chips";
import { useT } from "@/i18n";
import {
  PROMPT_CHIP_LABEL_KEY,
  settingsVoiceMessages,
} from "@/i18n/messages/settings-voice";
import { cn } from "@/lib/cn";
import { translateLanguageName } from "./voice-dictation-keys";

const ICON = "h-3 w-3 shrink-0";

function TranslateChip({
  language,
  onPick,
}: {
  /** The language it writes; null while Translate is off. */
  language: string | null;
  /** A language, or null to stop translating. */
  onPick: (language: string | null) => void;
}) {
  const languages =
    language && !TRANSLATE_LANGUAGES.includes(language)
      ? [...TRANSLATE_LANGUAGES, language]
      : TRANSLATE_LANGUAGES;
  const t = useT(settingsVoiceMessages);
  const StateIcon = language ? Icons.Check : Icons.Plus;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ToggleChip
          pressed={language !== null}
          testId="voice-prompt-chip-translate"
        >
          <StateIcon className={ICON} aria-hidden="true" />
          {language
            ? t("toLanguage", { language: translateLanguageName(language) })
            : t("chipTranslate")}
          <Icons.ChevronDown className={ICON} aria-hidden="true" />
        </ToggleChip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="min-w-[170px]"
      >
        {languages.map((entry) => (
          <DropdownMenuItem
            key={entry}
            onSelect={() => onPick(entry)}
            role="menuitemradio"
            aria-checked={entry === language}
            className={cn(
              "justify-between gap-2",
              entry === language && "bg-btn-ghost-fill text-primary",
            )}
            data-testid={`voice-prompt-language-${entry}`}
          >
            <span className="truncate">{translateLanguageName(entry)}</span>
            {entry === language ? (
              <Icons.Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : null}
          </DropdownMenuItem>
        ))}
        {language ? (
          <DropdownMenuItem
            onSelect={() => onPick(null)}
            className="text-muted"
            data-testid="voice-prompt-language-none"
          >
            {t("dontTranslate")}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function VoicePromptChips({
  text,
  onChange,
}: {
  text: string;
  onChange: (text: string) => void;
}) {
  const t = useT(settingsVoiceMessages);
  const on = new Set(chipsIn(text));
  return (
    <div
      role="group"
      aria-label={t("addToInstructions")}
      className="flex flex-wrap gap-1 px-2 pb-1.5 pt-0.5"
    >
      {PROMPT_CHIPS.map((chip) => {
        if (chip.id === "translate") {
          return (
            <TranslateChip
              key={chip.id}
              language={translateLanguageIn(text)}
              onPick={(language) =>
                onChange(toggleChip(text, "translate", language))
              }
            />
          );
        }
        const pressed = on.has(chip.id);
        const StateIcon = pressed ? Icons.Check : Icons.Plus;
        return (
          <ToggleChip
            key={chip.id}
            pressed={pressed}
            onClick={() => onChange(toggleChip(text, chip.id))}
            testId={`voice-prompt-chip-${chip.id}`}
          >
            <StateIcon className={ICON} aria-hidden="true" />
            {t(PROMPT_CHIP_LABEL_KEY[chip.id])}
          </ToggleChip>
        );
      })}
    </div>
  );
}
